// Mock data for fee management development and testing

import {
  GlobalFeeSettings,
  VendorSpecificFee,
  FeeHistory,
  Commission,
  DeliveryFee,
  ServiceCharge,
} from './types';

// Mock Global Fee Settings
export const mockGlobalFeeSettings: GlobalFeeSettings = {
  id: 'global-001',
  defaultCommissionRate: 15,
  defaultCommissionType: 'percentage',
  defaultBaseFee: 3400,
  defaultFeePerKM: 50,
  defaultFreeDistance: 1,
  defaultServiceChargeType: 'percentage',
  defaultServiceChargeValue: 2.5,
  transactionFee: 50,
  setupFee: 10000,
  monthlyFee: 5000,
  updatedAt: '2025-01-15T10:30:00Z',
  updatedBy: 'Admin',
};

// Mock Vendor Specific Fees
export const mockVendorSpecificFees: VendorSpecificFee[] = [
  {
    id: 'vendor-001',
    restaurantId: 'rest-001',
    restaurantName: 'Tasty Chillies',
    commission: {
      id: 'comm-001',
      restaurantId: 'rest-001',
      restaurantName: 'Tasty Chillies',
      commissionRate: 15,
      commissionType: 'percentage',
      isActive: true,
      createdAt: '2025-01-01T00:00:00Z',
      updatedAt: '2025-01-15T10:30:00Z',
    },
    deliveryModel: 'global',
    serviceCharge: 'global',
    performanceMetrics: {
      totalOrders: 456,
      totalRevenue: 12400 * 456,
      commissionEarned: (12400 * 456 * 0.15),
    },
    actions: ['Edit Fee'],
  },
  {
    id: 'vendor-002',
    restaurantId: 'rest-002',
    restaurantName: 'The Place Restaurant',
    commission: {
      id: 'comm-002',
      restaurantId: 'rest-002',
      restaurantName: 'The Place Restaurant',
      commissionRate: 10,
      commissionType: 'percentage',
      isActive: true,
      createdAt: '2025-01-01T00:00:00Z',
      updatedAt: '2025-01-10T14:20:00Z',
    },
    deliveryModel: 'custom',
    serviceCharge: 'custom',
    performanceMetrics: {
      totalOrders: 789,
      totalRevenue: 15500 * 789,
      commissionEarned: (15500 * 789 * 0.10),
    },
    actions: ['Edit Fee'],
  },
  {
    id: 'vendor-003',
    restaurantId: 'rest-003',
    restaurantName: 'Food Palace',
    commission: {
      id: 'comm-003',
      restaurantId: 'rest-003',
      restaurantName: 'Food Palace',
      commissionRate: 12,
      commissionType: 'percentage',
      isActive: true,
      createdAt: '2025-01-01T00:00:00Z',
      updatedAt: '2025-01-12T09:15:00Z',
    },
    deliveryModel: 'global',
    serviceCharge: 'global',
    performanceMetrics: {
      totalOrders: 234,
      totalRevenue: 8900 * 234,
      commissionEarned: (8900 * 234 * 0.12),
    },
    actions: ['Edit Fee'],
  },
];

// Mock Fee History
export const mockFeeHistory: FeeHistory[] = [
  {
    id: 'hist-001',
    changeType: 'commission',
    restaurantId: 'rest-001',
    restaurantName: 'Tasty Chillies',
    previousValue: { commissionRate: 12, commissionType: 'percentage' },
    newValue: { commissionRate: 15, commissionType: 'percentage' },
    changedBy: 'Admin User',
    changeReason: 'Performance-based adjustment',
    timestamp: '2025-01-15T10:30:00Z',
    status: 'active',
  },
  {
    id: 'hist-002',
    changeType: 'global',
    previousValue: { defaultCommissionRate: 12, defaultCommissionType: 'percentage' },
    newValue: { defaultCommissionRate: 15, defaultCommissionType: 'percentage' },
    changedBy: 'Super Admin',
    changeReason: 'Market adjustment',
    timestamp: '2025-01-14T16:45:00Z',
    status: 'active',
  },
  {
    id: 'hist-003',
    changeType: 'delivery',
    restaurantId: 'rest-002',
    restaurantName: 'The Place Restaurant',
    previousValue: { baseFee: 500, feePerKM: 40 },
    newValue: { baseFee: 600, feePerKM: 50 },
    changedBy: 'Operations Manager',
    changeReason: 'Fuel cost increase',
    timestamp: '2025-01-13T11:20:00Z',
    status: 'active',
  },
  {
    id: 'hist-004',
    changeType: 'service',
    restaurantId: 'rest-003',
    restaurantName: 'Food Palace',
    previousValue: { chargeType: 'percentage', chargeValue: 2.0 },
    newValue: { chargeType: 'percentage', chargeValue: 2.5 },
    changedBy: 'Finance Manager',
    changeReason: 'Service enhancement fee',
    timestamp: '2025-01-12T09:15:00Z',
    status: 'active',
  },
];

// Utility functions
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatPercentage = (value: number): string => {
  return `${value}%`;
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const calculateCommissionEarnings = (revenue: number, rate: number): number => {
  return revenue * (rate / 100);
};

export const getStatusBadgeColor = (status: string): string => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'inactive':
      return 'bg-gray-100 text-gray-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'reverted':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getDeliveryModelBadgeColor = (model: string): string => {
  switch (model) {
    case 'global':
      return 'bg-blue-100 text-blue-800';
    case 'custom':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getChangeTypeBadgeColor = (changeType: string): string => {
  switch (changeType) {
    case 'commission':
      return 'bg-orange-100 text-orange-800';
    case 'delivery':
      return 'bg-blue-100 text-blue-800';
    case 'service':
      return 'bg-green-100 text-green-800';
    case 'global':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Mock API delay simulation
export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// Mock API responses
export const mockApiResponse = <T>(data: T, delayMs: number = 500): Promise<T> => {
  return delay(delayMs).then(() => data);
};

// Error simulation
export const mockApiError = (message: string, delayMs: number = 500): Promise<never> => {
  return delay(delayMs).then(() => Promise.reject(new Error(message)));
};